import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useScrollToTop } from '../hooks/useScrollToTop';

const TermsOfUse: React.FC = () => {
  useScrollToTop();
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <div className="flex-grow py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <Link 
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>

            <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Use</h1>
            
            <div className="prose max-w-none">
              <p className="text-gray-600 mb-6">
                Last updated: {new Date().toLocaleDateString()}
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
                <p className="text-yellow-800 font-medium">IMPORTANT NOTICE</p>
                <p className="text-yellow-800 text-sm mt-1">
                  By accessing or using 11thadmissions.com, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our services.
                </p>
              </div>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">DISCLAIMER</h2>
                <p className="text-gray-600 mb-4">
                  11thadmission.com is an independent educational consulting service and is not affiliated with, endorsed by, or connected to any government body, educational institution, or admissions authority. We do not control, influence, or make decisions regarding any admission processes. Our services are limited to providing guidance and support only.
                </p>
                <p className="text-gray-600 mb-4">
                  All admission decisions are made solely by the respective educational institutions and their designated admissions committees. We cannot guarantee admission to any institution, and any suggestions or advice provided are based on our experience but do not represent official positions of any educational institution or governing body.
                </p>
                <p className="text-gray-600 mb-4">
                  By using our services, you acknowledge and agree that 11thadmissions.com shall not be held liable for any admission decisions, application outcomes, or related consequences. We make no warranties, expressed or implied, regarding admission results or acceptance to any educational institution.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Nature of Service</h2>
                <p className="text-gray-600 mb-4">
                  11thadmissions.com provides AI-powered predictions and guidance tools to help users explore college admission possibilities based on historical data and algorithmic analysis.
                </p>
                <p className="text-gray-600 mb-4">
                  All recommendations, predictions, cutoff estimates, college rankings, and other outputs provided by our platform are for informational purposes only and constitute neither guaranteed admission outcomes nor official advice.
                </p>
                <p className="text-gray-600 mb-4">
                  Our services are intended to supplement, not replace, official information from educational institutions, government bodies, or professional educational counselors.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Accuracy Disclaimer</h2>
                <p className="text-gray-600 mb-4">
                  While our models are built on historical trends and continuously improved, actual cutoffs and admission outcomes may differ due to various external factors including but not limited to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>Government policy changes</li>
                  <li>Institutional policy modifications</li>
                  <li>Demographic shifts</li>
                  <li>Demand fluctuations</li>
                  <li>Reservation category updates</li>
                  <li>Changes in examination patterns or scoring methodologies</li>
                  <li>Unforeseen circumstances affecting the educational landscape</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  11thadmissions.com expressly disclaims any warranty or guarantee regarding the accuracy, reliability, completeness, or timeliness of any predictions, recommendations, or information provided through our platform.
                </p>
                <p className="text-gray-600 mb-4">
                  Users acknowledge that all predictive models have inherent limitations and margins of error. Our cutoff predictions typically have a margin of error of ±2-5 percentage points, which users should consider when making decisions.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">3. No Liability for Admission Outcomes</h2>
                <p className="text-gray-600 mb-4">
                  Users expressly acknowledge and agree that all decisions made using information from this platform are at their own risk and discretion. 11thadmissions.com, its owners, operators, employees, agents, partners, and affiliates shall not be held responsible or liable, directly or indirectly, for:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>Rejection by any college or educational institution</li>
                  <li>Failure to secure admission in any preferred institution</li>
                  <li>Changes in cutoff patterns or admission criteria</li>
                  <li>Admission delays, cancellations, or denials</li>
                  <li>Any financial losses incurred due to reliance on our platform</li>
                  <li>Any emotional distress or psychological impact resulting from admission outcomes</li>
                  <li>Any consequences arising from decisions made based on information provided by our platform</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  Users are strongly advised to verify all information with official sources and consult with qualified educational counselors before making admission decisions.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">4. User Eligibility and Account Responsibilities</h2>
                <p className="text-gray-600 mb-4">
                  By using 11thadmissions.com, you represent and warrant that:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>You are at least 13 years of age</li>
                  <li>If you are under 18, you have obtained parental or guardian consent</li>
                  <li>You will provide accurate, current, and complete information during registration</li>
                  <li>You will maintain and promptly update your account information</li>
                  <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                  <li>You accept full responsibility for all activities occurring under your account</li>
                  <li>You will notify us immediately of any unauthorized use of your account</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  We reserve the right to terminate or suspend accounts that violate these terms or are inactive for an extended period.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Usage & Privacy</h2>
                <p className="text-gray-600 mb-4">
                  We follow industry best practices to protect your data. Uploaded marksheets, personal information, and academic records are stored securely and used only to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>Provide personalized college recommendations</li>
                  <li>Generate accurate cutoff predictions</li>
                  <li>Improve our algorithms and prediction models</li>
                  <li>Enhance platform functionality and user experience</li>
                  <li>Conduct anonymized statistical analysis for educational research</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  By using our services, you consent to the collection, processing, and storage of your data as outlined in our <Link to="/privacy-policy" className="text-[#7B4AE2] hover:underline">Privacy Policy</Link>, which is incorporated by reference into these Terms of Use.
                </p>
                <p className="text-gray-600 mb-4">
                  You retain ownership of your personal data and may request its deletion at any time, subject to our legal obligations to maintain certain records.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
                <p className="text-gray-600 mb-4">
                  All content on 11thadmissions.com, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software, is the property of 11thadmissions.com or its content suppliers and is protected by Indian and international copyright and intellectual property laws.
                </p>
                <p className="text-gray-600 mb-4">
                  College names, logos, or images used on the platform are for informational purposes only and remain the property of their respective institutions. We do not claim endorsement or affiliation with any educational institution unless explicitly stated.
                </p>
                <p className="text-gray-600 mb-4">
                  Users are granted a limited, non-exclusive, non-transferable, and revocable license to access and use our platform for personal, non-commercial purposes. This license does not include:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>Modifying, copying, or creating derivative works</li>
                  <li>Using any content for commercial purposes</li>
                  <li>Attempting to decompile, reverse engineer, or extract source code</li>
                  <li>Removing any copyright, trademark, or proprietary notices</li>
                  <li>Transferring the materials to another person or entity</li>
                  <li>Using data mining, robots, or similar data gathering methods</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Payment Terms & Refund Policy</h2>
                <p className="text-gray-600 mb-4">
                  All payments for premium services are final and non-refundable unless otherwise specified in our <Link to="/refund-policy" className="text-[#7B4AE2] hover:underline">Refund Policy</Link>.
                </p>
                <p className="text-gray-600 mb-4">
                  By making a payment, you agree to the following terms:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>You authorize us to charge the payment method you provide</li>
                  <li>You will provide current, complete, and accurate payment information</li>
                  <li>All prices are subject to change with notice</li>
                  <li>You are responsible for any taxes applicable to your purchase</li>
                  <li>Subscription terms and conditions are clearly displayed before purchase</li>
                  <li>Refunds may be considered on a case-by-case basis for technical issues preventing service delivery</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  Refund requests must be submitted within 7 days of purchase. Approved refunds will be processed within 10-15 business days.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">8. User Conduct</h2>
                <p className="text-gray-600 mb-4">
                  When using 11thadmissions.com, you agree not to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe upon the rights of others</li>
                  <li>Submit false, misleading, or inaccurate information</li>
                  <li>Upload or transmit viruses or malicious code</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with the proper functioning of the platform</li>
                  <li>Engage in any activity that could damage, disable, or impair our services</li>
                  <li>Use automated means to access or collect data from our platform</li>
                  <li>Create multiple accounts for deceptive or fraudulent purposes</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  We reserve the right to terminate or suspend access to our services for users who violate these conduct guidelines.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
                <p className="text-gray-600 mb-4">
                  To the fullest extent permitted by applicable law, 11thadmissions.com and its owners, directors, officers, employees, agents, partners, and licensors shall not be liable for:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>Any direct, indirect, incidental, special, consequential, or exemplary damages</li>
                  <li>Loss of profits, revenue, data, goodwill, or other intangible losses</li>
                  <li>Damages resulting from interruption of service or computer malfunction</li>
                  <li>Damages for personal injury, emotional distress, or mental suffering</li>
                  <li>Any damages arising from your use of or inability to use our services</li>
                  <li>Any damages resulting from unauthorized access to your personal data</li>
                  <li>Any damages resulting from errors, mistakes, or inaccuracies in our content</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  This limitation applies regardless of whether such damages arise from breach of contract, tort (including negligence), or any other legal theory, and regardless of whether we have been advised of the possibility of such damages.
                </p>
                <p className="text-gray-600 mb-4">
                  In jurisdictions that do not allow the exclusion or limitation of liability for consequential or incidental damages, our liability shall be limited to the maximum extent permitted by law.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Indemnification</h2>
                <p className="text-gray-600 mb-4">
                  You agree to indemnify, defend, and hold harmless 11thadmissions.com, its officers, directors, employees, agents, licensors, and suppliers from and against all losses, expenses, damages, and costs, including reasonable attorneys' fees, resulting from:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>Your violation of these Terms of Use</li>
                  <li>Your violation of any rights of another</li>
                  <li>Your violation of any applicable laws or regulations</li>
                  <li>Any claim related to your content or information</li>
                  <li>Any activity related to your account (including negligent or wrongful conduct)</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  This indemnification obligation will survive the termination of your relationship with 11thadmissions.com and these Terms of Use.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Disclaimer of Warranties</h2>
                <p className="text-gray-600 mb-4">
                  The services provided by 11thadmissions.com are provided "as is" and "as available" without warranties of any kind, either express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.
                </p>
                <p className="text-gray-600 mb-4">
                  11thadmissions.com, its subsidiaries, affiliates, and licensors do not warrant that:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>The service will function uninterrupted, secure, or available at any particular time or location</li>
                  <li>Any errors or defects will be corrected</li>
                  <li>The service is free of viruses or other harmful components</li>
                  <li>The results of using the service will meet your requirements</li>
                  <li>The information provided is complete, accurate, or reliable</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  Some jurisdictions do not allow the exclusion of certain warranties, so some of the above exclusions may not apply to you.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Governing Law</h2>
                <p className="text-gray-600 mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions.
                </p>
                <p className="text-gray-600 mb-4">
                  Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in Pune, Maharashtra, India.
                </p>
                <p className="text-gray-600 mb-4">
                  You agree to submit to the personal jurisdiction of the courts located in Pune, Maharashtra, India for the purpose of litigating all such claims.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Severability</h2>
                <p className="text-gray-600 mb-4">
                  If any provision of these Terms is found to be unenforceable or invalid under any applicable law, such unenforceability or invalidity shall not render these Terms unenforceable or invalid as a whole, and such provisions shall be deleted without affecting the remaining provisions herein.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">14. Changes to These Terms</h2>
                <p className="text-gray-600 mb-4">
                  We reserve the right to modify these terms at any time. We will notify users of any material changes via:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>Email notification to registered users</li>
                  <li>Prominent notice on our website</li>
                  <li>Update to the "Last updated" date at the top of these Terms</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  Your continued use of the platform after such modifications constitutes your acknowledgment and acceptance of the modified Terms. If you do not agree to the modified terms, you must stop using the platform.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">15. Termination</h2>
                <p className="text-gray-600 mb-4">
                  We may terminate or suspend your account and access to our services immediately, without prior notice or liability, for any reason, including but not limited to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-600">
                  <li>Violation of these Terms</li>
                  <li>Providing false information</li>
                  <li>Engaging in fraudulent activity</li>
                  <li>Failure to pay any fees when due</li>
                  <li>Upon request by law enforcement or government agencies</li>
                  <li>For technical or security reasons</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  Upon termination, your right to use the service will immediately cease. If you wish to terminate your account, you may simply discontinue using the service or contact us to request account deletion.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">16. Force Majeure</h2>
                <p className="text-gray-600 mb-4">
                  11thadmissions.com shall not be liable for any failure to perform its obligations where such failure is a result of acts of nature (including fire, flood, earthquake, storm, hurricane, or other natural disaster), war, invasion, act of foreign enemies, hostilities (whether war is declared or not), civil war, rebellion, revolution, insurrection, military or usurped power or confiscation, terrorist activities, government sanction, blockage, embargo, labor dispute, strike, lockout, or interruption or failure of electricity or telephone service.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">17. Contact Information</h2>
                <p className="text-gray-600">
                  Questions about the Terms of Use should be sent to:
                  <br />
                  Email: legal@11thadmissions.com
                  <br />
                  Address: 123 Education Street, Pune, Maharashtra, India
                </p>
              </section>

              <div className="bg-[#F0E9FF] p-6 rounded-lg mt-8">
                <p className="text-[#7B4AE2] font-semibold mb-2">Acceptance of Terms</p>
                <p className="text-gray-700">
                  By using 11thadmissions.com, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use. If you continue to use our services, you are indicating your acceptance of these terms and conditions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfUse;